import { useState, useEffect } from 'react';

const MEASUREMENT_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_ID || 'G-5634LNGB49';

// ─── Consent Helpers ───────────────────────────────────────────────

export const hasAnalyticsConsent = () => {
    const prefs = localStorage.getItem('cookiePreferences');
    if (!prefs) return false;
    try {
        return JSON.parse(prefs).analytics_storage === true;
    } catch {
        return false;
    }
};

export const useAnalyticsConsent = () => {
    const [consent, setConsent] = useState(hasAnalyticsConsent);

    useEffect(() => {
        const handleConsentChange = () => {
            setConsent(hasAnalyticsConsent());
        };
        window.addEventListener('cookieConsentUpdated', handleConsentChange);
        return () => window.removeEventListener('cookieConsentUpdated', handleConsentChange);
    }, []);

    return consent;
};

// ─── Initialization ────────────────────────────────────────────────

let _gaInitialized = false;

/**
 * Safely ensure and retrieve the gtag function
 */
export const getGtag = () => {
    if (typeof window === 'undefined') return null;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };
    }
    return window.gtag;
};

export const initGA = () => {
    if (_gaInitialized) return;
    if (typeof window === 'undefined') return;

    const gtag = getGtag();
    if (!gtag) return;

    // Check if gtag script is already loaded
    if (!document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
        document.head.appendChild(script);
    }

    gtag('js', new Date());
    gtag('config', MEASUREMENT_ID, {
        send_page_view: false,       // We track manually via PageTracker
        anonymize_ip: true,          // DSGVO: IP-Anonymisierung
        cookie_flags: 'SameSite=None;Secure',
        linker: { accept_incoming: false }, // No cross-domain _gl params
    });

    _gaInitialized = true;
};

// ─── Core Tracking (consent-aware) ─────────────────────────────────

/**
 * Send a GA4 event. Only fires if analytics consent is granted.
 * @param {string} eventName - GA4 event name (snake_case)
 * @param {Object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
    if (!hasAnalyticsConsent()) return;
    const gtag = getGtag();
    if (!gtag) return;
    gtag('event', eventName, params);
};

/**
 * Track a page view. Only fires if analytics consent is granted.
 * @param {string} path - Page path including search params
 */
export const trackPageView = (path) => {
    if (!hasAnalyticsConsent()) return;
    const gtag = getGtag();
    if (!gtag) return;
    gtag('event', 'page_view', {
        page_path: path,
        page_title: document.title,
    });
};

export const setAnalyticsUserId = (userId) => {
    if (!hasAnalyticsConsent() || !userId) return;
    const gtag = getGtag();
    if (!gtag) return;
    gtag('config', MEASUREMENT_ID, {
        user_id: userId
    });
};

/**
 * Set GA4 User Properties for Audience Building (Zielgruppen)
 * @param {Object} properties - Key-value pairs of user properties
 */
export const setAnalyticsUserProperties = (properties) => {
    if (!hasAnalyticsConsent() || !properties) return;
    const gtag = getGtag();
    if (!gtag) return;
    gtag('set', 'user_properties', properties);
};

// ─── Specific Event Helpers ────────────────────────────────────────

// Contact Form
export const trackContactFormSubmit = () => {
    trackEvent('submit_form', {
        form_type: 'contact',
        event_category: 'Contact',
    });
};

// Login
export const trackLogin = (method = 'email') => {
    trackEvent('login', {
        method: method,
        event_category: 'User',
    });
};

// Registration
export const trackRegistration = (method = 'email') => {
    trackEvent('sign_up', {
        method: method,
        event_category: 'User',
    });
};

// Event Registration Funnel
export const trackEventDetailView = (eventName, eventSlug) => {
    trackEvent('view_item', {
        item_name: eventName,
        item_category: 'Event',
        content_type: 'event_detail',
        event_slug: eventSlug,
    });
};

export const trackEventRegistrationStart = (eventName) => {
    trackEvent('begin_checkout', {
        item_name: eventName,
        event_category: 'Events',
        content_type: 'event_registration',
    });
};

export const trackEventRegistrationComplete = (eventName) => {
    trackEvent('purchase', {
        item_name: eventName,
        event_category: 'Events',
        content_type: 'event_registration',
        value: 1,
    });
};

// Blog Engagement
export const trackBlogPostView = (postTitle, postId) => {
    trackEvent('view_item', {
        item_name: postTitle,
        item_id: postId,
        item_category: 'Blog',
        content_type: 'blog_post',
    });
};

export const trackBlogPostCreate = () => {
    trackEvent('create_content', {
        content_type: 'blog_post',
        event_category: 'Blog',
    });
};

export const trackBlogReadProgress = (postTitle, percent) => {
    trackEvent('blog_read_progress', {
        post_title: postTitle,
        percent_scrolled: percent,
        event_category: 'Blog',
    });
};

// Team Member
export const trackTeamMemberView = (memberName) => {
    trackEvent('view_item', {
        item_name: memberName,
        item_category: 'Team',
        content_type: 'team_member',
    });
};

// CTA Clicks
export const trackCTAClick = (ctaName, ctaLocation) => {
    trackEvent('cta_click', {
        cta_name: ctaName,
        cta_location: ctaLocation,
        event_category: 'Engagement',
    });
};

// Navigation
export const trackNavigationClick = (linkName, linkTarget) => {
    trackEvent('navigation_click', {
        link_name: linkName,
        link_target: linkTarget,
        event_category: 'Navigation',
    });
};

// Social / Share
export const trackSocialClick = (platform) => {
    trackEvent('share', {
        method: platform,
        event_category: 'Social',
    });
};

// Outbound Links
export const trackOutboundLink = (url) => {
    trackEvent('click', {
        link_url: url,
        link_domain: new URL(url).hostname,
        outbound: true,
        event_category: 'Outbound',
    });
};

// Document Downloads
export const trackDocumentDownload = (documentName) => {
    trackEvent('file_download', {
        file_name: documentName,
        event_category: 'Documents',
    });
};

// Donation page
export const trackDonationPageView = () => {
    trackEvent('view_item', {
        item_category: 'Donation',
        content_type: 'donation_page',
    });
};

// ─── Dashboard / Admin Events ──────────────────────────────────────

export const trackAdminEventCreated = (eventName) => {
    trackEvent('admin_action', {
        action_type: 'create_event',
        item_name: eventName,
        event_category: 'Admin',
    });
};

export const trackAdminEventUpdated = (eventName) => {
    trackEvent('admin_action', {
        action_type: 'update_event',
        item_name: eventName,
        event_category: 'Admin',
    });
};

export const trackAdminBlogCreated = (postTitle) => {
    trackEvent('admin_action', {
        action_type: 'create_blog_post',
        item_name: postTitle,
        event_category: 'Admin',
    });
};

export const trackAdminBlogUpdated = (postTitle) => {
    trackEvent('admin_action', {
        action_type: 'update_blog_post',
        item_name: postTitle,
        event_category: 'Admin',
    });
};

export const trackAdminUserPromoted = (role) => {
    trackEvent('admin_action', {
        action_type: 'promote_user',
        role_assigned: role,
        event_category: 'Admin',
    });
};

// ─── Engagement Tracking (Scroll, Time, Exit) ──────────────────────

/**
 * Track scroll depth milestones (25%, 50%, 75%, 100%).
 * Call this once per page – it automatically handles thresholds.
 * @param {string} pageName - Human-readable page name for reports
 * @returns {Function} cleanup function to remove listener
 */
export const trackScrollDepth = (pageName) => {
    if (!hasAnalyticsConsent()) return () => {};

    const milestones = [25, 50, 75, 100];
    const reached = new Set();

    const handler = () => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return;

        const percent = Math.round((scrollTop / docHeight) * 100);

        milestones.forEach(m => {
            if (percent >= m && !reached.has(m)) {
                reached.add(m);
                trackEvent('scroll', {
                    percent_scrolled: m,
                    page_name: pageName,
                    event_category: 'Engagement',
                });
            }
        });
    };

    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
};

/**
 * Track time-on-page milestones (15s, 30s, 60s, 120s, 300s).
 * @param {string} pageName - Human-readable page name
 * @returns {Function} cleanup function to clear timers
 */
export const trackTimeOnPage = (pageName) => {
    if (!hasAnalyticsConsent()) return () => {};

    const milestones = [15, 30, 60, 120, 300]; // seconds
    const timers = [];

    milestones.forEach(seconds => {
        const timer = setTimeout(() => {
            trackEvent('time_on_page', {
                seconds_elapsed: seconds,
                page_name: pageName,
                event_category: 'Engagement',
            });
        }, seconds * 1000);
        timers.push(timer);
    });

    return () => timers.forEach(t => clearTimeout(t));
};

/**
 * Track exit intent (mouse leaves viewport on desktop).
 * Only fires once per page view.
 * @param {string} pageName - Human-readable page name
 * @returns {Function} cleanup function
 */
export const trackExitIntent = (pageName) => {
    if (!hasAnalyticsConsent()) return () => {};

    let fired = false;

    const handler = (e) => {
        if (fired) return;
        // Only trigger when mouse leaves from the top of the viewport
        if (e.clientY <= 0) {
            fired = true;
            trackEvent('exit_intent', {
                page_name: pageName,
                event_category: 'Engagement',
            });
        }
    };

    document.addEventListener('mouseleave', handler);
    return () => document.removeEventListener('mouseleave', handler);
};

// ─── Legacy compatibility (deprecated, remove after migration) ─────

export const trackJoinClick = (location = 'unknown') => {
    trackCTAClick('join_button', location);
};

export const trackEventRegistration = (eventName) => {
    trackEventRegistrationComplete(eventName);
};

// Particle tracking (kept for backward compatibility)
export const trackParticleInteractionStart = () => {
    trackEvent('interaction_start', {
        event_category: 'Particles',
        interaction_type: 'particle_background',
    });
};

export const trackParticleInteractionEnd = (durationSeconds) => {
    trackEvent('interaction_end', {
        event_category: 'Particles',
        interaction_type: 'particle_background',
        duration_seconds: Math.round(durationSeconds),
    });
};

export const trackParticleInteractionDuration = (durationSeconds) => {
    trackEvent('interaction_duration', {
        event_category: 'Particles',
        interaction_type: 'particle_background',
        duration_seconds: Math.round(durationSeconds),
    });
};

